/**
 * Pre-seeded tax registration number formats for the Country config drawer (Ticket-348). Every entry
 * is a starting point that an admin can override: the backend validates only that the pattern compiles
 * and creators' numbers are tested against whatever is saved. Whitespace is stripped before testing.
 */
export interface RegistrationPattern {
	countryCode: string;
	/** The number's local name, used as the statement label ("ABN 12 345 678 901"). */
	label: string;
	/** Plain-language format shown in the picker. */
	description: string;
	pattern: string;
}

export const REGISTRATION_PATTERNS: readonly RegistrationPattern[] = [
	{ countryCode: "AU", label: "ABN", description: "11 digits", pattern: "^\\d{11}$" },
	{ countryCode: "NZ", label: "GST number", description: "8 or 9 digits (IRD number)", pattern: "^\\d{8,9}$" },
	{
		countryCode: "SG",
		label: "GST registration number",
		description: "UEN, e.g. 200912345A or M90312345A",
		pattern: "^(\\d{8,9}[A-Z]|[A-Z]\\d{8}[A-Z]|[TSR]\\d{2}[A-Z]{2}\\d{4}[A-Z])$",
	},
	{
		countryCode: "MY",
		label: "SST number",
		description: "e.g. W10-1808-32000012",
		pattern: "^[A-Z]\\d{2}-\\d{4}-\\d{8}$",
	},
	{
		countryCode: "ID",
		label: "NPWP",
		description: "15 or 16 digits, dots and dash optional",
		pattern: "^(\\d{15,16}|\\d{2}\\.\\d{3}\\.\\d{3}\\.\\d-\\d{3}\\.\\d{3})$",
	},
	{ countryCode: "TH", label: "Tax ID", description: "13 digits", pattern: "^\\d{13}$" },
	{
		countryCode: "VN",
		label: "Tax code",
		description: "10 digits, optional 3-digit branch",
		pattern: "^\\d{10}(-\\d{3})?$",
	},
	{
		countryCode: "PH",
		label: "TIN",
		description: "9 digits plus optional branch code",
		pattern: "^\\d{3}-?\\d{3}-?\\d{3}(-?\\d{3,5})?$",
	},
	{ countryCode: "HK", label: "Business registration number", description: "8 digits", pattern: "^\\d{8}$" },
	{ countryCode: "TW", label: "Unified business number", description: "8 digits", pattern: "^\\d{8}$" },
	{
		countryCode: "JP",
		label: "Invoice registration number",
		description: "T followed by 13 digits",
		pattern: "^T?\\d{13}$",
	},
	{
		countryCode: "KR",
		label: "Business registration number",
		description: "10 digits, e.g. 123-45-67890",
		pattern: "^\\d{3}-?\\d{2}-?\\d{5}$",
	},
	{
		countryCode: "IN",
		label: "GSTIN",
		description: "15 characters, e.g. 22AAAAA0000A1Z5",
		pattern: "^\\d{2}[A-Z]{5}\\d{4}[A-Z][1-9A-Z]Z[0-9A-Z]$",
	},
	{ countryCode: "AE", label: "TRN", description: "15 digits", pattern: "^\\d{15}$" },
	{
		countryCode: "GB",
		label: "VAT number",
		description: "9 or 12 digits, GB prefix optional",
		pattern: "^(GB)?\\d{9}(\\d{3})?$",
	},
	{
		countryCode: "IE",
		label: "VAT number",
		description: "7 digits plus 1–2 letters, IE prefix optional",
		pattern: "^(IE)?\\d{7}[A-Z]{1,2}$",
	},
	{ countryCode: "DE", label: "USt-IdNr.", description: "9 digits, DE prefix optional", pattern: "^(DE)?\\d{9}$" },
	{
		countryCode: "FR",
		label: "TVA number",
		description: "2 characters plus 9 digits, FR prefix optional",
		pattern: "^(FR)?[A-Z0-9]{2}\\d{9}$",
	},
	{
		countryCode: "NL",
		label: "BTW number",
		description: "9 digits, B, 2 digits; NL prefix optional",
		pattern: "^(NL)?\\d{9}B\\d{2}$",
	},
	{
		countryCode: "ES",
		label: "NIF-IVA",
		description: "letter or digit, 7 digits, letter or digit",
		pattern: "^(ES)?[A-Z0-9]\\d{7}[A-Z0-9]$",
	},
	{
		countryCode: "IT",
		label: "Partita IVA",
		description: "11 digits, IT prefix optional",
		pattern: "^(IT)?\\d{11}$",
	},
	{
		countryCode: "CH",
		label: "UID / MWST",
		description: "CHE plus 9 digits",
		pattern: "^(CHE-?)?\\d{3}\\.?\\d{3}\\.?\\d{3}$",
	},
	{ countryCode: "US", label: "EIN", description: "9 digits, e.g. 12-3456789", pattern: "^\\d{2}-?\\d{7}$" },
	{
		countryCode: "CA",
		label: "GST/HST number",
		description: "9 digits, optional RT0001",
		pattern: "^\\d{9}(RT\\d{4})?$",
	},
	{ countryCode: "MX", label: "RFC", description: "12 or 13 characters", pattern: "^[A-ZÑ&]{3,4}\\d{6}[A-Z0-9]{3}$" },
	{
		countryCode: "BR",
		label: "CNPJ",
		description: "14 digits, e.g. 12.345.678/0001-95",
		pattern: "^\\d{2}\\.?\\d{3}\\.?\\d{3}\\/?\\d{4}-?\\d{2}$",
	},
	{ countryCode: "ZA", label: "VAT number", description: "10 digits starting with 4", pattern: "^4\\d{9}$" },
];

export const registrationPatternFor = (countryCode: string): RegistrationPattern | null =>
	REGISTRATION_PATTERNS.find((p) => p.countryCode === countryCode.toUpperCase()) ?? null;
